import React from "react";
import { useTranslation } from 'react-i18next';
import { Modal, Button, Input, List, Typography, Table, Spin, Form, Space, Alert } from 'antd';
import { EyeOutlined, QrcodeOutlined, PlusOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import { URL_BARCODE, URL_BOM } from '../../constance/urls';
import * as _ from 'lodash';
import { Toast } from '../../utils/Toast';
import { GET, POST } from '../../utils/HttpClient';
import { defaultBarcodeDetail } from '../../constance/dataTypes';
import '../../antd.css';
import ColorButton from '../../components/common/ColorButton';
import { GREEN, BLUE_COLOR } from '../../constance/color';

const DEBOUNCE_DELAY_MILLI_SEC = 500;
const BARCODE_LENGTH = 14;

export default function BOMModal(props) {
  const {
    open,
    onClose,
    transaction,
  } = props;

  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessages, setErrorMessages] = React.useState(null);
  const [selectedBarcodes, setSelectedBarcodes] = React.useState([]);

  // search barcode
  const [search, setSearch] = React.useState('');

  // detail barcode
  const [showDetailBarcode, setShowDetailBarcode] = React.useState(null)
  const [barcodeDetail, setBarcodeDetail] = React.useState(defaultBarcodeDetail);
  const { Title } = Typography;

  const handleSubmit = async () => {
    setErrorMessages(null);
    setIsLoading(true);
    try {
      await POST(`${URL_BOM.BOM_TRANSACTION}${transaction}${URL_BOM.ADD_BARCODE}`, { 'barcodes': selectedBarcodes.join(',') })
      Toast.success(t('success'))
      onClose();
    } catch (error) {
      setErrorMessages(error.errorMessages);
    } finally {
      setIsLoading(false);
    }
  }

  const fetchBarcodeDetail = async (barcode) => {
    setErrorMessages(null);
    setIsLoading(true);
    setBarcodeDetail(defaultBarcodeDetail);
    try {
      const response = await GET(URL_BARCODE.BARCODE, { search: barcode })
      setBarcodeDetail(_.get(response, 'data.0', {}))
    } catch (error) {
      setErrorMessages(error.errorMessages);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddBarcode = () => {
    if (search && search.length >= BARCODE_LENGTH) {
      setSearch('');
      setSelectedBarcodes([...selectedBarcodes, search]);
    }
  }

  React.useEffect(() => {
    if (!open) {
      setSearch("");
      setSelectedBarcodes([]);
      setShowDetailBarcode(null);
    }
    else
      if (showDetailBarcode) {
        fetchBarcodeDetail(showDetailBarcode);
      }
  }, [open, showDetailBarcode]);

  React.useEffect(() => {
    if (search && search.length >= BARCODE_LENGTH) {
      handleAddBarcode();
    }
  }, [search]);


  return (
    <div>
      <Modal
        title={t(`bom_transactions.boms.add`)}
        visible={open}
        onCancel={onClose}
        footer={null}
        centered>
        {
          errorMessages && (
            <Alert
              message={t('error')}
              description={errorMessages}
              type="error"
              showIcon
            />
          )
        }
        <div>
          <Form layout="vertical">
            <Form.Item label={t('barcodes.barcode_id')}>
              <Space.Compact style={{
                width: '100%',
              }}>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Button
                  type='primary'
                  icon={<PlusOutlined />}
                  onClick={handleAddBarcode}
                >
                  {t('export_transactions.add')}
                </Button>
              </Space.Compact>
            </Form.Item>
          </Form>
        </div>
        {selectedBarcodes.length ? (
          <List
            size="large"
            header={<Title level={4}>{t('barcodes.list')}</Title>}
            dataSource={selectedBarcodes}
            renderItem={(selectedBarcode, idx) => (
              <List.Item
                actions={[
                  <ColorButton
                    variant="outlined"
                    color='primary'
                    override={BLUE_COLOR}
                    icon={<EyeOutlined />}
                    onClick={() => setShowDetailBarcode(selectedBarcode)}
                  />,
                  <Button
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setSelectedBarcodes(selectedBarcodes.filter((_, i) => i !== idx));
                    }}
                    danger
                  />,
                ]}
              >
                {selectedBarcode}
              </List.Item>
            )}
          />
        ) : null}
        <div style={{ textAlign: 'right' }}>
          <Button
            variant="filled"
            color="default"
            icon={<CloseOutlined />}
            onClick={onClose}
            loading={isLoading}
          >
            {t('cancel')}
          </Button>
          <ColorButton
            variant="solid"
            color='primary'
            override={GREEN}
            icon={<PlusOutlined />}
            onClick={handleSubmit}
            disabled={!selectedBarcodes.length}
            loading={isLoading}
            style={{ marginLeft: '10px' }}
          >
            {t('add')}
          </ColorButton>
        </div>
      </Modal>

      <Modal
        title={<><QrcodeOutlined /> {t(`barcodes.barcode_id`)}: {showDetailBarcode}</>}
        visible={!!showDetailBarcode}
        onCancel={() => setShowDetailBarcode(null)}
        footer={null}
        centered
      >
        {isLoading ? (
          <Spin size="large" />
        ) : (
          <Table
            columns={[
              {
                title: t('wood_imports.wood_code'),
                dataIndex: 'wood_code',
                key: 'wood_code',
              },
              {
                title: t('barcodes.amount'),
                dataIndex: 'amount',
                key: 'amount',
              },
            ]}
            dataSource={_.get(barcodeDetail, 'wood_size_amount_map_list', [])}
            rowKey={(record) => record.wood_code}
            pagination={false}
            bordered
          />
        )}
      </Modal>
    </div>
  );
}

